import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v3';
import type { DepositionalWikiPartsFragment } from '~/apollo/generated/v3/graphql';
import { Panel } from '~/components/common/Panel';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { useZodForm } from '~/utils/forms';
import {
  depositionalWikiSchema,
  initialDepositionalWiki,
} from '~/utils/modules/depositionalWiki';
import { DepositionalWikiFormFields } from './depositional-wiki-form-fields';

export const UPDATE_DEPOSITIONAL_WIKI = graphql(`
  mutation UpdateDepositionalWiki(
    $id: Int!
    $depositional: DepositionalWikiInput!
  ) {
    updateDepositionalWiki(depositional: $depositional, id: $id) {
      ...depositionalWikiParts
    }
  }
`);

export function UpdateDepositionalWikiForm({
  wikiPage,
}: {
  wikiPage: DepositionalWikiPartsFragment;
}) {
  const [updateWikiPage, { loading, error }] = useMutation(
    UPDATE_DEPOSITIONAL_WIKI,
  );

  const form = useZodForm({
    schema: depositionalWikiSchema,
    values: initialDepositionalWiki(wikiPage),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateWikiPage({
        variables: {
          id: wikiPage.id,
          depositional: values,
        },
      });
      toast.success('Wiki page saved successfully.');
    } catch (err) {
      console.log('Error saving wiki page', err);
      toast.error('There was a problem saving the wiki page.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <Panel>
          <Panel.Heading>
            <Panel.Title>Wiki Page Details</Panel.Title>
          </Panel.Heading>
          <Panel.Body className="space-y-4">
            <DepositionalWikiFormFields />
            <HookFormErrors graphQLError={error} />
          </Panel.Body>
          <Panel.Footer align="right">
            <Button type="submit" color="primary" loading={loading}>
              Save
            </Button>
          </Panel.Footer>
        </Panel>
      </form>
    </FormProvider>
  );
}
